import { useEffect, useRef, useState } from "react";

/**
 * Ticker – minimal, jump‑free marquee driven by requestAnimationFrame (RAF).
 *
 * Core idea:
 *  - Measure one content "pass" (width of items and spacer) = one cycle distance.
 *  - If that distance is wider than the container, we scroll left at constant px/s.
 *  - We render the content twice only when scrolling, so it loops seamlessly.
 *  - When content/size changes, we re‑measure and preserve the current phase
 *    by keeping the translateX offset modulo the new distance (no visual jump).
 *
 * Props:
 *  - children: ReactNode – the ticker items for a single pass
 *  - speed: number – pixels per second (default 50). Higher = faster.
 *  - spacer: number – px gap appended after one pass before the duplicate begins.
 */
const Ticker = ({ children, speed = 50, spacer = 32 }) => {
  // === Refs that hold DOM nodes ===
  const wrapRef = useRef(null); // outer container that defines available width
  const trackRef = useRef(null); // inner element we translate with CSS transform
  const passRef = useRef(null); // first pass (one set of items) that we measure

  // === Refs used by the RAF engine ===
  const rafRef = useRef(null); // current RAF id (so we can cancel on clean-up)
  const lastTimestamp = useRef(null); // timestamp of previous RAF frame (for distance)
  const offset = useRef(0); // current translateX in px (negative values scroll left)
  const distanceRef = useRef(0); // width of one cycle

  // === Reactive UI state ===
  const [scrolling, setScrolling] = useState(false); // whether content overflows and should scroll

  /**
   * measure() – computes the cycle distance and whether we need to scroll.
   * - Reads natural width of the pass via scrollWidth (sufficient for inline-flex without wrapping).
   * - Adds `spacer` to form the cycle distance.
   * - Compares with wrapper width; if longer, enable scrolling.
   * - Preserves the current phase when the distance changes by wrapping `offset`
   *   modulo the new distance (so we never pop back to start).
   */
  const measure = () => {
    const wrap = wrapRef.current;
    const pass = passRef.current;
    if (!wrap || !pass) return;

    const width = pass.scrollWidth || 0; // width of one pass
    const distance = spacer + width; // one full cycle distance = pass width + spacer that visually separates loops
    const wrapWidth = wrap.clientWidth || 0; // container width for comparison (layout width, no transforms)
    const prev = distanceRef.current || 0; // preserve phase: keep the current offset within the new cycle distance

    distanceRef.current = distance;
    if (prev > 0 && distance > 0) {
      // `offset` is negative while moving left; wrap it cleanly for the new `distance`.
      offset.current = -((-offset.current % prev) % distance);
    }

    // Scroll only if the content is wider than the container (tolerance +1 px for rounding)
    setScrolling(distance > wrapWidth + 1);
  };

  /**
   * step(timestamp) – RAF loop. Moves the track left at constant `speed` (px/s).
   * - Computes time delta from last frame.
   * - Updates `offset` and applies modulo wrapping by `distance` for continuous loop.
   * - Applies transform to the track.
   */
  const step = (timestamp) => {
    if (!scrolling) return; // guard: in static mode we don't run RAF

    // Initialize frame timer on first call
    if (lastTimestamp.current == null) lastTimestamp.current = timestamp;

    const dt = (timestamp - lastTimestamp.current) / 1000; // seconds since last frame
    lastTimestamp.current = timestamp;

    const distance = distanceRef.current || 0;
    if (distance > 0) {
      // Move left by speed * dt
      offset.current -= speed * dt;

      // Wrap smoothly when we surpass one full cycle
      if (-offset.current >= distance) {
        // add back one or more full cycles in one go (handles slow frames)
        offset.current += distance * Math.floor(-offset.current / distance);
      }

      const track = trackRef.current;
      if (track) track.style.transform = `translateX(${offset.current}px)`;
    }

    rafRef.current = requestAnimationFrame(step);
  };

  /**
   * Start/stop the RAF loop whenever `scrolling` toggles.
   * - In static mode, we cancel RAF and reset transform to 0.
   * - In scrolling mode, we (re)start RAF from the current `offset`.
   */
  useEffect(() => {
    const track = trackRef.current;

    if (!scrolling) {
      // Reset transform so content sits at the start when it fits
      if (track) track.style.transform = "translateX(0px)";
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimestamp.current = null;
      return;
    }

    // Start the loop (or resume after remeasure)
    if (!rafRef.current) {
      lastTimestamp.current = null; // ensure `dt` is fresh on first frame
      rafRef.current = requestAnimationFrame(step);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolling, speed]);

  /**
   * When `children` change: re‑measure next frame.
   * Using a frame delay ensures DOM has committed before reading sizes.
   */
  useEffect(() => {
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [children]);

  /**
   * Setup listeners:
   * - ResizeObserver on the pass and wrapper → catches content and container width changes (new data)
   * - run an initial measure on mount
   */
  useEffect(() => {
    // Observe size changes of the measured pass and the wrapper
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      if (passRef.current) ro.observe(passRef.current);
      if (wrapRef.current) ro.observe(wrapRef.current);
    }

    measure(); // First measure
    return () => {
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <div ref={wrapRef} className="ticker w-full overflow-hidden">
      <div ref={trackRef} className="inline-flex will-change-transform">
        {/* First pass (width measured) */}
        <span
          ref={passRef}
          className="inline-flex shrink-0 items-center gap-8"
          style={{ marginRight: `${spacer}px` }}
        >
          {children}
        </span>

        {/* Duplicate only when scrolling - seamless wrap */}
        {scrolling && (
          <span className="inline-flex shrink-0 items-center gap-8">
            {children}
          </span>
        )}
      </div>
    </div>
  );
};

export default Ticker;
